import ExpeditionChapterHeading from "./ExpeditionChapterHeading";
import IconLink from "./IconLink";
import { siteUrl } from "./site";

export default function ExpeditionBattleDesign() {
  return <section id="battle-design" className="expedition-case" aria-labelledby="expedition-battle-design-title">
    <div className="container expedition-case-inner">
      <ExpeditionChapterHeading number="06" label="BATTLE / EVENT DESIGN" id="expedition-battle-design-title" title="전투 이벤트 구조 설계" technology="설계안 비교 · 턴 순환 · 이벤트 분배" />
      <div className="expedition-case-content">
        <p className="expedition-design-intro">턴제 시스템 담당자와 전투 이벤트를 어떤 단위로 나눌지 정하기 위해 두 설계안을 제안하고 장단점을 비교했습니다. 이를 바탕으로 턴이 돌아가는 흐름과 이벤트를 나눠 보내는 방식을 초기 설계로 정리했습니다. 전투 시스템 코드는 팀원이 작성했습니다.</p>
        <div className="expedition-battle-options" aria-label="논의한 두 가지 이벤트 구조">
          <article>
            <p className="expedition-battle-option-label">설계안 01 · 한곳에서 처리</p>
            <h4>하나의 이벤트 클래스와 구조체</h4>
            <p>이벤트를 구조체 단위로 구성하고, 이벤트 매니저가 하나의 이벤트 클래스를 호출하면 내부에서 필요한 처리를 수행하는 방식입니다.</p>
            <dl><div><dt>장점</dt><dd>구조가 단순하고, 처리 방식이 직관적입니다.</dd></div><div><dt>고려할 점</dt><dd>새 이벤트나 기능을 추가할 때마다 한 클래스의 코드가 길어져, 뒤로 갈수록 전체 흐름을 읽기 어려워질 수 있습니다.</dd></div></dl>
          </article>
          <article>
            <p className="expedition-battle-option-label">설계안 02 · 이벤트별로 분리</p>
            <h4>흐름을 정하는 매니저와 개별 이벤트</h4>
            <p>이벤트 매니저가 흐름을 정하고 필요한 이벤트를 호출합니다. 각 이벤트는 가급적 하나의 흐름을 맡고, 세부 기능은 내부에서 처리하도록 나누는 방식입니다.</p>
            <dl><div><dt>장점</dt><dd>이벤트를 구성할 때 흐름이 명확하고, 분리된 이벤트를 비교적 자유롭게 조립할 수 있습니다.</dd></div><div><dt>고려할 점</dt><dd>이벤트 사이의 연결과 매니저의 책임을 정해야 하므로 구조가 더 복잡해지고, 초기 구현에도 시간이 더 필요합니다.</dd></div></dl>
          </article>
        </div>
        <div className="expedition-battle-plan" aria-labelledby="expedition-battle-flow-title">
          <div className="expedition-battle-plan-heading"><h4 id="expedition-battle-flow-title">초기 설계도</h4><span>턴 순환 · 이벤트 분배</span></div>
          <p className="expedition-battle-implementation">턴 큐에서 다음 행동 주체를 꺼내고, 개발자가 설정한 전투 단계에 따라 행동을 실행한 뒤 결과로 다음 턴을 판단합니다. UI와 카메라는 전투 규칙을 판단하지 않고, 진행·연출·표시 이벤트를 받아 보이는 부분만 바꿉니다.</p>
          <div className="expedition-battle-initial" aria-label="턴 큐에서 이벤트 상태, 행동 실행, 결과 반환으로 이어지고 결과에 따라 다음 턴으로 돌아갑니다. 이벤트 상태는 진행, 연출, 표시 이벤트를 보냅니다.">
            <div className="expedition-flow">
              <div><strong>턴 큐</strong><span>다음 행동 주체</span></div>
              <div><strong>이벤트 상태</strong><span>개발자가 설정한 전투 단계</span></div>
              <div><strong>행동 실행</strong><span>액션 처리</span></div>
              <div><strong>결과 반환</strong><span>다음 턴 · 전투 종료 판단</span></div>
            </div>
            <p className="expedition-battle-loop">↻ 결과에 따라 다음 턴으로 순환</p>
            <div className="expedition-battle-events">
              <article><h5>진행 이벤트</h5><p>턴 시작, 행동 시작, 결과 정리</p></article>
              <article><h5>연출 이벤트</h5><p>카메라 포커스, 액션 컷, 기본 구도 복귀</p></article>
              <article><h5>표시 이벤트</h5><p>UI 갱신, 턴 순서 표시</p></article>
            </div>
          </div>
          <details className="expedition-evidence">
            <summary>실제 구현과 비교 보기</summary>
            <p>구현된 코드에서는 BattleController가 중앙 진행자 역할을 맡아 일반 공격·스킬·아이템으로 실행을 나누고, 실행이 끝났는지 확인한 뒤 다음 턴으로 넘깁니다.</p>
            <figure className="expedition-frame expedition-battle-diagram">
              <IconLink href={siteUrl('/images/expedition-33/battle-action-flow.svg')} target="_blank" rel="noopener noreferrer">
                <picture>
                  <source media="(max-width: 600px)" srcSet={siteUrl('/images/expedition-33/battle-action-flow-mobile.svg')} />
                  <img src={siteUrl('/images/expedition-33/battle-action-flow.svg')} alt="선택·대상 지정 상태에서 행동 요청을 보내면 BattleController가 일반 공격, 스킬, 아이템으로 분기합니다. 일반 공격과 스킬은 BattleActionExecutor와 SkillScript를 사용하고, 아이템은 Execute_Item에서 효과를 처리합니다. 실행 완료는 컨트롤러가 확인해 다음 턴으로 이어갑니다." width="1120" height="800" loading="lazy" />
                </picture>
              </IconLink>
              <figcaption>구현 코드의 호출 관계. 그림을 누르면 크게 볼 수 있습니다.</figcaption>
            </figure>
          </details>
        </div>
      </div>
    </div>
  </section>;
}
